import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  createAction,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from 'state/store';
import {
  TaskListElement,
  TaskListFilters,
  TasksListTableSorting,
  TaskPermissionsMap,
} from 'types/store/tasksList';
import { SLICE_STATUS } from 'utils/constants';
import { Status } from 'utils/customTypes';
import {
  getFetchCentralizedTasksListQueryParams,
  normalizeFiltersSettingsVersions,
} from '../helpers';
import { fetchTasksList } from 'api/tasksList';
import {
  setTasksAssignees,
  setTasksProjects,
  selectAssigneesListByIds,
  selectTaskProjectById,
} from '../allTasks/allTasksSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import {
  CENTRALIZED_TASKS_TABLE_TABS,
  DEFAULT_TASK_ELEMENT_PERMISSIONS,
} from 'utils/constants/centralizedTasks';

const myTasksAdapter = createEntityAdapter<TaskListElement>();

interface MyTasksState {
  tasks: EntityState<TaskListElement>;
  filters: TaskListFilters;
  sorting: TasksListTableSorting;
  status: Status;
  links: {
    next: string | null;
  };
  totalCount?: number;
  permissions: TaskPermissionsMap;
}

const initialState: MyTasksState = {
  tasks: myTasksAdapter.getInitialState(),
  permissions: {},
  filters: {},
  sorting: {
    sortBy: 'name',
    order: 'desc',
  },
  status: SLICE_STATUS.IDLE,
  links: {
    next: null,
  },
};

interface FetchMyTasksParams {
  userId: string;
  fetchNext?: boolean;
  includeTotalCount?: boolean;
}

export const fetchMyTasks = createAsyncThunk(
  'normalized/tasksList/myTasks/fetchMyTasks',
  async (
    {
      userId,
      fetchNext = false,
      includeTotalCount = false,
    }: FetchMyTasksParams,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;
      let nextLink = null;
      const filters = state.normalized.tasksList.myTasks.filters;
      const sorting = state.normalized.tasksList.myTasks.sorting;
      if (fetchNext) {
        nextLink = state.normalized.tasksList.myTasks.links.next;
      }
      const queryParams = getFetchCentralizedTasksListQueryParams(
        { userId, nextLink, includeTotalCount },
        filters,
        sorting
      );
      const response = await fetchTasksList(queryParams);
      dispatch(setTasksAssignees(response.assignees));
      dispatch(setTasksProjects(response.projects));
      return {
        tasks: response.tasks,
        links: response.links,
        totalCount: response.totalCount,
        fetchNext,
        permissions: response.permissions,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Failed to fetch user tasks');
    }
  }
);

export const updateTaskInList = createAction<{
  taskId: string;
  updatedTask: Partial<TaskListElement>;
}>('normalized/tasksList/myTasks/updateTaskInList');

const myTasksSlice = createSlice({
  name: 'normalized/tasksList/myTasks',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        search: state.filters.search,
        ...action.payload,
      };
    },
    setSorting: (state, action) => {
      state.sorting = action.payload;
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMyTasks.pending, (state) => {
      state.status = SLICE_STATUS.LOADING;
    });
    builder
      .addCase(fetchMyTasks.fulfilled, (state, action) => {
        state.status = SLICE_STATUS.IDLE;
        if (action.payload) {
          const fetchNext = action.payload.fetchNext;
          if (fetchNext) {
            myTasksAdapter.upsertMany(state.tasks, action.payload.tasks);
            state.permissions = {
              ...state.permissions,
              ...action.payload.permissions,
            };
          } else {
            myTasksAdapter.setAll(state.tasks, action.payload.tasks);
            state.permissions = action.payload.permissions || {};
          }
          state.links = action.payload.links;
          if (action.payload.totalCount !== undefined) {
            state.totalCount = action.payload.totalCount;
          }
        }
      })
      .addCase(fetchMyTasks.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(fetchFilterSettingByType.fulfilled, (state, action) => {
        if (
          action.payload &&
          action.payload.filter_type === CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS
        ) {
          state.filters = normalizeFiltersSettingsVersions(
            action.payload.filter_settings
          );
        }
      })
      .addCase(updateTaskInList, (state, action) => {
        if (action.payload) {
          const { taskId, updatedTask } = action.payload;
          const existingTask = myTasksAdapter
            .getSelectors()
            .selectById(state.tasks, taskId);
          if (existingTask) {
            myTasksAdapter.updateOne(state.tasks, {
              id: taskId,
              changes: updatedTask,
            });
          }
        }
      });
  },
});

const { selectAll: selectTasks } = myTasksAdapter.getSelectors(
  (state: RootState) => state.normalized.tasksList.myTasks.tasks
);

const selectMyTaskPermissions = (state: RootState) =>
  state.normalized.tasksList.myTasks.permissions;

export const selectFetchMyTasksStatus = (state: RootState) =>
  state.normalized.tasksList.myTasks.status;

export const selectMyTasks = createSelector(
  [(state: RootState) => state, selectTasks, selectMyTaskPermissions],
  (state, tasks, permissions) => {
    return tasks.map((task: TaskListElement) => ({
      ...task,
      assignees: selectAssigneesListByIds(state, task.assignees),
      project: selectTaskProjectById(state, task.projectId),
      permissions: permissions[task.id] ?? DEFAULT_TASK_ELEMENT_PERMISSIONS,
    }));
  }
);

export const selectMyTasksTableSorting = (state: RootState) =>
  state.normalized.tasksList.myTasks.sorting;

export const selectMyTasksFilters = (state: RootState) =>
  state.normalized.tasksList.myTasks.filters;

export const selectCanFetchMoreMyTasks = (state: RootState) =>
  !!state.normalized.tasksList.myTasks.links.next;

export const selectMyTasksTotalCount = (state: RootState) =>
  state.normalized.tasksList.myTasks.totalCount;

export const { setFilters, setSorting, setSearchFilter } = myTasksSlice.actions;

export default myTasksSlice.reducer;
